import axios from 'axios';
import React, { Component } from 'react';
import requireAuth from './requireAuth';

class RepzioOrders extends Component {
  constructor(props) {
    super(props);
    this.state = {
      orderID: '',
      loading: false,
      error: null,
      originalOrder: null,
      editedOrder: null,
      deletedItems: [],
      showDiff: false,
      submitting: false,
      submitResult: null,
      // alert default
      alertOverride: '1',
    };
    this.fetchOrder = this.fetchOrder.bind(this);
    this.handleHeaderChange = this.handleHeaderChange.bind(this);
    this.handleItemChange = this.handleItemChange.bind(this);
    this.handleDeleteItem = this.handleDeleteItem.bind(this);
    this.handleUndoDelete = this.handleUndoDelete.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleConfirmSave = this.handleConfirmSave.bind(this);
    this.buildDiff = this.buildDiff.bind(this);
  }

  componentDidMount() {
    const params = new URLSearchParams(this.props.location.search);
    const orderID = params.get('orderID');
    if (orderID) {
      this.setState({ orderID }, () => this.fetchOrder());
    }
  }

  async fetchOrder() {
    const { orderID } = this.state;
    if (!orderID.trim()) return;

    this.setState({
      loading: true,
      error: null,
      originalOrder: null,
      editedOrder: null,
      deletedItems: [],
      showDiff: false,
      submitResult: null,
    });

    try {
      const { data } = await axios.get(
        `/api/apprise/repzio/ab/orders/raw?orderID=${orderID.trim()}`,
      );

      const order = data.DATA.ORDER[0];
      this.setState({
        loading: false,
        originalOrder: JSON.parse(JSON.stringify(order)),
        editedOrder: JSON.parse(JSON.stringify(order)),
      });
    } catch (e) {
      this.setState({
        loading: false,
        error: e.response ? e.response.data : 'Failed to fetch order',
      });
    }
  }

  handleHeaderChange(field, value) {
    this.setState((prevState) => {
      const updated = { ...prevState.editedOrder, [field]: value };

      // Auto-sync mirrors
      if (field === 'Order_Class') {
        updated.User_Defined_8 = value;
      }
      if (field === 'Sales_Representative_1') {
        updated.User_Defined_9 = value;
      }

      return { editedOrder: updated };
    });
  }

  handleItemChange(index, field, value) {
    this.setState((prevState) => {
      const items = [...prevState.editedOrder.ITEMS];
      items[index] = { ...items[index], [field]: value };
      return { editedOrder: { ...prevState.editedOrder, ITEMS: items } };
    });
  }

  handleDeleteItem(index) {
    this.setState((prevState) => ({
      deletedItems: [...prevState.deletedItems, index],
    }));
  }

  handleUndoDelete(index) {
    this.setState((prevState) => ({
      deletedItems: prevState.deletedItems.filter((i) => i !== index),
    }));
  }

  buildDiff() {
    const { originalOrder, editedOrder, deletedItems } = this.state;
    const headerChanges = {};
    const itemChanges = [];

    // Only diff header fields that are editable or auto-synced
    const diffableFields = [
      'Customer_Code',
      'Order_Class',
      'Sales_Representative_1',
      'Sales_Representative_2',
      'Ship_To_Name',
      'Ship_To_Address_1',
      'Ship_To_City',
      'Ship_To_State',
      'Ship_To_Postal',
      'Ship_To_Country',
      'Bill_To_Name',
      'Bill_To_Address_1',
      'Bill_To_City',
      'Bill_To_State',
      'Bill_To_Postal',
      'Bill_To_Country',
      'Phone_Number',
      'Credit_Card_Type',
      'User_Defined_8',
      'User_Defined_9',
    ];

    diffableFields.forEach((field) => {
      if (originalOrder[field] !== editedOrder[field]) {
        headerChanges[field] = {
          from: originalOrder[field],
          to: editedOrder[field],
        };
      }
    });

    // Compare item fields
    originalOrder.ITEMS.forEach((originalItem, idx) => {
      if (deletedItems.includes(idx)) {
        itemChanges.push({
          lineNum: String(idx + 1),
          _delete: true,
          fields: {},
        });
        return;
      }
      const editedItem = editedOrder.ITEMS[idx];
      const changedFields = {};
      Object.keys(originalItem).forEach((field) => {
        if (originalItem[field] !== editedItem[field]) {
          changedFields[field] = {
            from: originalItem[field],
            to: editedItem[field],
          };
        }
      });
      if (Object.keys(changedFields).length > 0) {
        itemChanges.push({
          lineNum: String(idx + 1),
          _delete: false,
          fields: changedFields,
        });
      }
    });

    return { headerChanges, itemChanges };
  }

  handleSave() {
    this.setState({ showDiff: true, submitResult: null });
  }

  async handleConfirmSave() {
    const { orderID, alertOverride } = this.state;
    const { headerChanges, itemChanges } = this.buildDiff();

    const body = {};

    // Add header changes
    Object.keys(headerChanges).forEach((field) => {
      body[field] = headerChanges[field].to;
    });

    // If Customer_Code changed, add v=true to skip validation
    if (headerChanges.Customer_Code) {
      body.v = true;
    }

    // Add item changes using _line (1-based)
    if (itemChanges.length > 0) {
      body.ITEMS = itemChanges
        .filter((item) => !item._delete)
        .map((item) => {
          const changed = { _line: parseInt(item.lineNum, 10) };
          Object.keys(item.fields).forEach((field) => {
            changed[field] = item.fields[field].to;
          });
          return changed;
        });

      // Handle deleted items - set Qty_Ordered to "0"
      const deletedItemObjects = itemChanges
        .filter((item) => item._delete)
        .map((item) => ({
          _line: parseInt(item.lineNum, 10),
          Qty_Ordered: '0',
        }));

      if (deletedItemObjects.length > 0) {
        body.ITEMS = [...(body.ITEMS || []), ...deletedItemObjects];
      }
    }

    if (Object.keys(body).length === 0) {
      this.setState({ showDiff: false });
      return;
    }

    // Build query string - only orderID and optional alert
    let queryString = `orderID=${orderID.trim()}`;
    if (alertOverride === '0') queryString += `&alert=0`;

    this.setState({ submitting: true });

    try {
      await axios.put(
        `/api/apprise/repzio/ab/orders?${queryString}`,
        Object.keys(body).length > 0 ? body : undefined,
      );
      window.location.href = 'https://developer.abhomeinc.com/repzio-orders';
    } catch (e) {
      this.setState({
        submitting: false,
        submitResult: {
          success: false,
          data: e.response ? e.response.data : 'Failed to save',
        },
      });
    }
  }

  renderSearchBar() {
    return (
      <div className="row" style={{ marginTop: '20px' }}>
        <div className="col s12 m8 offset-m2">
          <div className="input-field inline" style={{ width: '70%' }}>
            <input
              type="text"
              placeholder="Enter Order ID (User_Defined_3)"
              value={this.state.orderID}
              onChange={(e) => this.setState({ orderID: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && this.fetchOrder()}
            />
          </div>
          <button
            type="button"
            className="btn waves-effect waves-light"
            style={{ marginLeft: '10px' }}
            onClick={this.fetchOrder}
            disabled={this.state.loading}
          >
            {this.state.loading ? 'Loading...' : 'Fetch Order'}
          </button>
        </div>
      </div>
    );
  }

  renderHeader() {
    const { originalOrder, editedOrder } = this.state;
    const editableFields = [
      'Customer_Code',
      'Order_Class',
      'Sales_Representative_1',
      'Sales_Representative_2',
      'Ship_To_Name',
      'Ship_To_Address_1',
      'Ship_To_City',
      'Ship_To_State',
      'Ship_To_Postal',
      'Ship_To_Country',
      'Bill_To_Name',
      'Bill_To_Address_1',
      'Bill_To_City',
      'Bill_To_State',
      'Bill_To_Postal',
      'Bill_To_Country',
      'Phone_Number',
      'Credit_Card_Type',
    ];

    return (
      <div className="section">
        <h5>Order Header</h5>
        <div className="row">
          {editableFields.map((field) => {
            const isChanged = originalOrder[field] !== editedOrder[field];
            return (
              <div className="col s12 m6" key={field}>
                <div className="input-field">
                  <input
                    type="text"
                    id={`hdr-${field}`}
                    value={editedOrder[field] || ''}
                    onChange={(e) =>
                      this.handleHeaderChange(field, e.target.value)
                    }
                    style={isChanged ? { backgroundColor: '#fff9c4' } : {}}
                  />
                  <label htmlFor={`hdr-${field}`} className="active">
                    {field}
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderItems() {
    const { originalOrder, editedOrder, deletedItems } = this.state;
    const itemFields = [
      'Product_Code',
      'Qty_Ordered',
      'Unit_of_Measure_Usage_Name',
      'Price',
      'Cancel_After_Date',
      'User_Defined_1',
    ];

    return (
      <div className="section">
        <h5>Line Items ({editedOrder.ITEMS.length})</h5>
        <table className="striped responsive-table">
          <thead>
            <tr>
              <th>#</th>
              {itemFields.map((f) => (
                <th key={f}>{f}</th>
              ))}
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {editedOrder.ITEMS.map((item, idx) => {
              const isDeleted = deletedItems.includes(idx);
              return (
                <tr
                  key={`item-${item.Product_Code}-${idx}`}
                  style={
                    isDeleted
                      ? { opacity: 0.4, textDecoration: 'line-through' }
                      : {}
                  }
                >
                  <td>{idx + 1}</td>
                  {itemFields.map((field) => {
                    const isChanged =
                      originalOrder.ITEMS[idx] &&
                      originalOrder.ITEMS[idx][field] !== item[field];
                    return (
                      <td key={field}>
                        <input
                          type="text"
                          value={item[field] || ''}
                          onChange={(e) =>
                            this.handleItemChange(idx, field, e.target.value)
                          }
                          disabled={isDeleted}
                          style={
                            isChanged && !isDeleted
                              ? { backgroundColor: '#fff9c4' }
                              : {}
                          }
                        />
                      </td>
                    );
                  })}
                  <td>
                    {isDeleted ? (
                      <button
                        type="button"
                        className="btn-small waves-effect waves-light green"
                        onClick={() => this.handleUndoDelete(idx)}
                      >
                        Undo
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn-small waves-effect waves-light red"
                        onClick={() => this.handleDeleteItem(idx)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  renderDiff() {
    const { headerChanges, itemChanges } = this.buildDiff();
    const hasChanges =
      Object.keys(headerChanges).length > 0 || itemChanges.length > 0;

    return (
      <div className="section">
        <h5>Changes Summary</h5>
        {!hasChanges && <p>No changes detected.</p>}
        {Object.keys(headerChanges).length > 0 && (
          <div>
            <h6>Header Changes:</h6>
            <ul className="collection">
              {Object.keys(headerChanges).map((field) => (
                <li className="collection-item" key={field}>
                  <strong>{field}:</strong>{' '}
                  <span
                    style={{ color: 'red', textDecoration: 'line-through' }}
                  >
                    {headerChanges[field].from}
                  </span>{' '}
                  →{' '}
                  <span style={{ color: 'green' }}>
                    {headerChanges[field].to}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {itemChanges.length > 0 && (
          <div>
            <h6>Item Changes:</h6>
            <ul className="collection">
              {itemChanges.map((item) => (
                <li
                  className="collection-item"
                  key={`change-item-${item.lineNum}`}
                >
                  {item._delete ? (
                    <span>
                      <strong>Line {item.lineNum}:</strong>{' '}
                      <span style={{ color: 'red' }}>
                        DELETED (Qty_Ordered → 0)
                      </span>
                    </span>
                  ) : (
                    <span>
                      <strong>Line {item.lineNum}:</strong>{' '}
                      {Object.keys(item.fields).map((field) => (
                        <span key={field} style={{ marginRight: '10px' }}>
                          {field}:{' '}
                          <span
                            style={{
                              color: 'red',
                              textDecoration: 'line-through',
                            }}
                          >
                            {item.fields[field].from}
                          </span>{' '}
                          →{' '}
                          <span style={{ color: 'green' }}>
                            {item.fields[field].to}
                          </span>
                        </span>
                      ))}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div style={{ marginTop: '20px' }}>
          <button
            type="button"
            className="btn waves-effect waves-light green"
            onClick={this.handleConfirmSave}
            disabled={!hasChanges || this.state.submitting}
          >
            {this.state.submitting ? 'Submitting...' : 'Confirm & Submit'}
          </button>
          <button
            type="button"
            className="btn waves-effect waves-light grey"
            style={{ marginLeft: '10px' }}
            onClick={() => this.setState({ showDiff: false })}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  renderSubmitResult() {
    const { submitResult } = this.state;
    if (!submitResult) return null;

    return (
      <div
        className={`card-panel ${submitResult.success ? 'green' : 'red'} lighten-4`}
        style={{ marginTop: '10px' }}
      >
        <span>
          {submitResult.success
            ? 'Order updated successfully!'
            : `Error: ${JSON.stringify(submitResult.data)}`}
        </span>
      </div>
    );
  }

  render() {
    const { editedOrder, error, showDiff } = this.state;

    return (
      <div>
        {this.renderSearchBar()}
        {error && (
          <div className="card-panel red lighten-4">
            <span>{JSON.stringify(error)}</span>
          </div>
        )}
        {this.renderSubmitResult()}
        {editedOrder && !showDiff && (
          <div>
            {this.renderHeader()}
            {this.renderItems()}
            <div className="section">
              <button
                type="button"
                className="btn waves-effect waves-light blue"
                onClick={this.handleSave}
              >
                Save Changes
              </button>
            </div>
          </div>
        )}
        {showDiff && this.renderDiff()}
      </div>
    );
  }
}

export default requireAuth(RepzioOrders);
